import { Fragment, useState, type ReactNode } from 'react';
import { Badge } from '@rudi/ui/badge';
import { Button } from '@rudi/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@rudi/ui/table';
import { PageHeader } from '../components/page';
import { usePerformanceOperations } from '../api/hooks';
import { AgentToolLatencySection } from './AgentToolLatencySection';
import type {
  PerformanceBreachDetail,
  PerformanceComponentCosts,
  PerformanceEvidenceLink,
  PerformanceFilterOptions,
  PerformanceInteractionOverview,
  PerformanceLabBudgetVerdict,
  PerformanceLabRunSummary,
  PerformanceLabUntrustedReason,
  PerformanceOperationsResponse,
  PerformanceQueryParams,
  PerformanceStatus,
  PerformanceWaiverDetail,
} from '../api/types';

type DimensionFilterKey = Exclude<keyof PerformanceQueryParams, 'days' | 'tenant'>;

const FILTERS: Array<{
  key: DimensionFilterKey;
  options: Exclude<keyof PerformanceFilterOptions, 'tenants'>;
  label: string;
}> = [
  { key: 'journey', options: 'journeys', label: 'Journey' },
  { key: 'interaction', options: 'interactions', label: 'Interaction' },
  { key: 'version', options: 'versions', label: 'Application version' },
  { key: 'browser', options: 'browsers', label: 'Browser' },
  { key: 'device', options: 'devices', label: 'Device' },
  { key: 'region', options: 'regions', label: 'Region' },
  { key: 'connection', options: 'connections', label: 'Connection' },
  { key: 'cache', options: 'caches', label: 'Cache state' },
  { key: 'complexity', options: 'complexities', label: 'Complexity' },
];

const PERFORMANCE_STATUS_PRESENTATION = {
  healthy: { label: 'Healthy', variant: 'default' },
  breached: { label: 'Breached', variant: 'destructive' },
  waived: { label: 'Waived', variant: 'secondary' },
  'insufficient-data': { label: 'Insufficient data', variant: 'outline' },
} as const satisfies Record<
  PerformanceStatus,
  { label: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }
>;

/** Rudi pools field values over 7 days unless asked otherwise, so the page
 *  sends `days` only for another window (ADR 0076 in Rudi). */
const DEFAULT_WINDOW_DAYS = 7;
const WINDOW_OPTIONS = [1, 7, 14, 30] as const;

/** The column count of the interactions table, for the details row. */
const INTERACTION_COLUMNS = 10;

const UNTRUSTED_REASON_LABELS: Record<string, string> = {
  redeployed: 'Rudi was redeployed during the walk',
  'under-load': 'the host was under load',
  'walk-failed': 'the walk failed',
} satisfies Record<PerformanceLabUntrustedReason, string>;

const BUDGET_VERDICT_LABELS = {
  same: 'Same as the budget',
  faster: 'Faster than the budget',
  slower: 'Slower than the budget',
  new: 'New step, no budget yet',
} as const satisfies Record<PerformanceLabBudgetVerdict, string>;

function milliseconds(value: number | null | undefined): string {
  return value === null || value === undefined ? 'No data' : `${Math.round(value)} ms`;
}

function days(count: number): string {
  return count === 1 ? '1 day' : `${count} days`;
}

function shortCommit(commit: string): string {
  return commit.slice(0, 7);
}

function dateTime(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
    timeZoneName: 'short',
  }).format(new Date(value));
}

function shortDate(value: string): string {
  return new Intl.DateTimeFormat('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(value));
}

function ComponentCosts({ costs }: { costs: PerformanceComponentCosts }) {
  return (
    <div>
      <h3 className="text-sm font-semibold">Component costs</h3>
      <div className="mt-2 grid gap-2 text-sm sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-3xl bg-muted px-3 py-2">
          Backend {milliseconds(costs.backendMs)}
        </div>
        <div className="rounded-3xl bg-muted px-3 py-2">
          Network {milliseconds(costs.networkMs)}
        </div>
        <div className="rounded-3xl bg-muted px-3 py-2">
          Frontend {milliseconds(costs.frontendMs)}
        </div>
        <div className="rounded-3xl bg-muted px-3 py-2">
          Environment {milliseconds(costs.environmentMs)}
        </div>
      </div>
    </div>
  );
}

function EvidenceLinks({ links }: { links: PerformanceEvidenceLink[] }) {
  return (
    <div className="flex flex-wrap gap-3 text-sm">
      {links.map(link => (
        <a
          key={link.uri}
          className="font-medium text-primary underline underline-offset-4"
          href={link.uri}
          rel="noreferrer"
          target="_blank"
        >
          {link.label}
        </a>
      ))}
    </div>
  );
}

function OperationalRecord({
  title,
  record,
}: {
  title: 'Breach' | 'Waiver';
  record: PerformanceBreachDetail | PerformanceWaiverDetail;
}) {
  return (
    <section className="rounded-3xl border bg-background p-4">
      <h3 className="text-sm font-semibold">{title}</h3>
      <dl className="mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-3">
        <div>
          <dt className="text-muted-foreground">Owner</dt>
          <dd>{record.owner}</dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Scope</dt>
          <dd>
            {Object.entries(record.scope)
              .map(([key, value]) => `${key}: ${value}`)
              .join(', ')}
          </dd>
        </div>
        <div>
          <dt className="text-muted-foreground">Remediation issue</dt>
          <dd>{record.remediationIssue}</dd>
        </div>
        <div className="sm:col-span-2">
          <dt className="text-muted-foreground">Reason</dt>
          <dd>{record.reason}</dd>
        </div>
        {'expiresAt' in record && (
          <div>
            <dt className="text-muted-foreground">Expires</dt>
            <dd>{new Date(record.expiresAt).toLocaleString()}</dd>
          </div>
        )}
      </dl>
      {record.evidence.length > 0 && (
        <div className="mt-3">
          <EvidenceLinks links={record.evidence} />
        </div>
      )}
    </section>
  );
}

function TelemetryScope({
  data,
  windowDays,
}: {
  data: PerformanceOperationsResponse;
  windowDays: number;
}) {
  if (data.scope === undefined) {
    return <>Collection status is managed independently from session replay.</>;
  }
  if (data.scope === 'platform') {
    const tenantName = data.tenantId
      ? (data.filters.tenants?.find(tenant => tenant.id === data.tenantId)?.name ?? data.tenantId)
      : null;
    return tenantName ? (
      <>
        Real-user samples from {tenantName} over the last {days(windowDays)}.
      </>
    ) : (
      <>Real-user samples pooled from every tenant over the last {days(windowDays)}.</>
    );
  }
  return <>Real-user samples from your tenant over the last {days(windowDays)}.</>;
}

function LatestPerfWalk({ run }: { run: PerformanceLabRunSummary | null | undefined }) {
  return (
    <section className="rounded-3xl border bg-card p-5" aria-label="Latest perf walk">
      <h2 className="font-semibold">Latest perf walk</h2>
      {run === undefined && (
        <p className="mt-1 text-sm text-muted-foreground">
          This Rudi release does not report perf walks.
        </p>
      )}
      {run === null && (
        <p className="mt-1 text-sm text-muted-foreground">
          This environment runs no perf walk, so only real-user values are shown.
        </p>
      )}
      {run && (
        <div className="mt-2 space-y-2 text-sm">
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-muted-foreground">
            <span>Commit {shortCommit(run.commit)}</span>
            <span>Finished {dateTime(run.finishedAt)}</span>
            <span>{run.samplesPerStep.toLocaleString()} samples per step</span>
          </div>
          <p className={run.trusted ? 'text-emerald-800' : 'text-destructive'}>
            {run.trusted
              ? 'Trusted'
              : `Not trusted: ${
                  run.untrustedReasons.length > 0
                    ? run.untrustedReasons
                        .map(reason => UNTRUSTED_REASON_LABELS[reason] ?? reason)
                        .join(', ')
                    : 'no reason given'
                }. The page shows the latest trusted walk per step.`}
          </p>
          {run.reportUri && (
            <a
              className="font-medium text-primary underline underline-offset-4"
              href={run.reportUri}
              rel="noreferrer"
              target="_blank"
            >
              Perf walk report
            </a>
          )}
        </div>
      )}
    </section>
  );
}

function TestedCells({ interaction }: { interaction: PerformanceInteractionOverview }) {
  const lab = interaction.lab;
  if (!lab) {
    return (
      <TableCell colSpan={3} className="text-muted-foreground">
        Not tested
      </TableCell>
    );
  }
  return (
    <>
      <TableCell>{milliseconds(lab.p50Ms)}</TableCell>
      <TableCell>
        <div>{lab.p95Ms === null ? 'Needs 50 samples' : milliseconds(lab.p95Ms)}</div>
        {lab.meetsTarget !== null && lab.targetMs !== null && (
          <div className="text-xs text-muted-foreground">
            {lab.meetsTarget ? 'Within' : 'Over'} {milliseconds(lab.targetMs)} target
          </div>
        )}
      </TableCell>
      <TableCell>{lab.sampleCount.toLocaleString()}</TableCell>
    </>
  );
}

function ReferenceLab({ interaction }: { interaction: PerformanceInteractionOverview }) {
  const lab = interaction.lab;
  if (lab) {
    return (
      <div>
        <h3 className="text-sm font-semibold">Reference lab</h3>
        <p className="mt-2 text-sm">
          {`${milliseconds(lab.p50Ms)} p50, ${milliseconds(lab.p90Ms)} p90, ${milliseconds(lab.p95Ms)} p95 from ${lab.sampleCount.toLocaleString()} samples in the latest trusted perf walk`}
        </p>
        <dl className="mt-3 grid gap-x-6 gap-y-3 text-sm sm:grid-cols-2 lg:grid-cols-4">
          <div>
            <dt className="text-muted-foreground">Commit</dt>
            <dd>{shortCommit(lab.commit)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Measured</dt>
            <dd>{dateTime(lab.measuredAt)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Slowest sample</dt>
            <dd>{milliseconds(lab.maxMs)}</dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Lab target</dt>
            <dd>
              {lab.targetMs === null
                ? 'No target'
                : `${milliseconds(lab.targetMs)}${
                    lab.meetsTarget === null ? '' : lab.meetsTarget ? ', met' : ', missed'
                  }`}
            </dd>
          </div>
          <div>
            <dt className="text-muted-foreground">Budget verdict</dt>
            <dd>{lab.budgetVerdict ? BUDGET_VERDICT_LABELS[lab.budgetVerdict] : 'No verdict'}</dd>
          </div>
        </dl>
      </div>
    );
  }
  return (
    <div>
      <h3 className="text-sm font-semibold">Reference lab</h3>
      <p className="mt-2 text-sm">
        {interaction.labPercentiles === null || interaction.labPercentiles === undefined
          ? 'No blessed lab result available'
          : `${milliseconds(interaction.labPercentiles.p50Ms)} p50, ${milliseconds(interaction.labPercentiles.p95Ms)} p95, ${milliseconds(interaction.labPercentiles.p99Ms)} p99 from ${(interaction.labSampleCount ?? 0).toLocaleString()} samples (${interaction.labStatus ?? 'no-data'})`}
      </p>
    </div>
  );
}

function FieldTrend({ interaction }: { interaction: PerformanceInteractionOverview }) {
  const trend = interaction.trend ?? [];
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold">Field trend</h3>
      <p className="mt-1 text-xs text-muted-foreground">Real users, one row per day</p>
      {trend.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No daily trend data available</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <Table aria-label={`${interaction.interactionLabel} field trend`}>
            <TableHeader>
              <TableRow>
                <TableHead>Day</TableHead>
                <TableHead>p50</TableHead>
                <TableHead>p95</TableHead>
                <TableHead>p99</TableHead>
                <TableHead>Samples</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trend.map(point => (
                <TableRow key={point.windowEnd}>
                  <TableCell>{shortDate(point.windowEnd)}</TableCell>
                  <TableCell>{milliseconds(point.percentiles.p50Ms)}</TableCell>
                  <TableCell>{milliseconds(point.percentiles.p95Ms)}</TableCell>
                  <TableCell>{milliseconds(point.percentiles.p99Ms)}</TableCell>
                  <TableCell>{point.sampleCount.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function LabTrend({ interaction }: { interaction: PerformanceInteractionOverview }) {
  const trend = interaction.labTrend ?? [];
  return (
    <div className="min-w-0">
      <h3 className="text-sm font-semibold">Perf walk trend</h3>
      <p className="mt-1 text-xs text-muted-foreground">Tested, one row per trusted walk</p>
      {trend.length === 0 ? (
        <p className="mt-2 text-sm text-muted-foreground">No perf walk results available</p>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <Table aria-label={`${interaction.interactionLabel} perf walk trend`}>
            <TableHeader>
              <TableRow>
                <TableHead>Walk</TableHead>
                <TableHead>Commit</TableHead>
                <TableHead>p50</TableHead>
                <TableHead>p95</TableHead>
                <TableHead>Samples</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {trend.map(point => (
                <TableRow key={`${point.measuredAt}-${point.commit}`}>
                  <TableCell>{shortDate(point.measuredAt)}</TableCell>
                  <TableCell>{shortCommit(point.commit)}</TableCell>
                  <TableCell>{milliseconds(point.p50Ms)}</TableCell>
                  <TableCell>{milliseconds(point.p95Ms)}</TableCell>
                  <TableCell>{point.sampleCount.toLocaleString()}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function InteractionDetails({
  interaction,
  windowDays,
}: {
  interaction: PerformanceInteractionOverview;
  windowDays: number;
}) {
  return (
    <div className="space-y-4">
      <p className="text-sm font-medium">
        {interaction.baselineDeltaMs === null
          ? 'No baseline comparison available'
          : `${interaction.baselineDeltaMs > 0 ? '+' : ''}${Math.round(interaction.baselineDeltaMs)} ms vs baseline`}
      </p>
      <div>
        <h3 className="text-sm font-semibold">Real users</h3>
        <p className="mt-2 text-sm">
          {`${milliseconds(interaction.percentiles.p50Ms)} p50, ${milliseconds(interaction.percentiles.p95Ms)} p95, ${milliseconds(interaction.percentiles.p99Ms)} p99 from ${interaction.sampleCount.toLocaleString()} samples over the last ${days(windowDays)}`}
          {interaction.fieldTargetP95Ms !== null && interaction.fieldTargetP95Ms !== undefined
            ? `, against a p95 target of ${milliseconds(interaction.fieldTargetP95Ms)}`
            : ''}
        </p>
      </div>
      <ReferenceLab interaction={interaction} />
      <div className="grid gap-4 lg:grid-cols-2">
        <FieldTrend interaction={interaction} />
        <LabTrend interaction={interaction} />
      </div>
      <ComponentCosts costs={interaction.components} />
      {interaction.breach && <OperationalRecord title="Breach" record={interaction.breach} />}
      {interaction.waiver && <OperationalRecord title="Waiver" record={interaction.waiver} />}
    </div>
  );
}

function FilterSelect({
  id,
  label,
  value,
  onChange,
  children,
}: {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  children: ReactNode;
}) {
  return (
    <label className="space-y-1 text-sm" htmlFor={id}>
      <span className="font-medium">{label}</span>
      <select
        id={id}
        className="h-10 w-full rounded-3xl border border-input bg-background px-3"
        value={value}
        onChange={event => onChange(event.target.value)}
      >
        {children}
      </select>
    </label>
  );
}

export function PerformancePage() {
  const [filters, setFilters] = useState<PerformanceQueryParams>({});
  const { data, error, isLoading } = usePerformanceOperations(filters);
  const [expandedInteraction, setExpandedInteraction] = useState<string | null>(null);

  function setFilter<K extends keyof PerformanceQueryParams>(
    key: K,
    value: PerformanceQueryParams[K] | undefined
  ) {
    setFilters(previous => {
      if (value === undefined) {
        const next = { ...previous };
        delete next[key];
        return next;
      }
      return { ...previous, [key]: value };
    });
  }

  // An older Rudi reads one day's rollup and omits the window.
  const windowDays = data?.windowDays ?? 1;
  const minimumSamples = data?.minimumSamples ?? 0;
  const tenants = data?.filters.tenants ?? [];
  const showTenantFilter =
    data?.scope === 'platform' && (tenants.length > 0 || filters.tenant !== undefined);
  const tenantOptions =
    filters.tenant !== undefined && !tenants.some(tenant => tenant.id === filters.tenant)
      ? [...tenants, { id: filters.tenant, name: filters.tenant }]
      : tenants;

  return (
    <div>
      <PageHeader
        title="Performance"
        description="What real users saw beside what the perf walk measured, against the current contract."
      />
      {isLoading && (
        <div className="py-12 text-center text-sm text-muted-foreground">
          Loading performance data…
        </div>
      )}
      {error && !isLoading && (
        <div role="alert" className="rounded-3xl border border-destructive/40 bg-destructive/5 p-5">
          <p className="font-medium text-destructive">Could not load performance data.</p>
          <p className="mt-1 text-sm text-muted-foreground">
            {error instanceof Error ? error.message : 'The performance API returned an error.'}
          </p>
        </div>
      )}
      {data && (
        <div className="space-y-6">
          <section className="rounded-3xl border bg-card p-5" aria-label="Tenant telemetry status">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className="font-semibold">Tenant performance telemetry</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  <TelemetryScope data={data} windowDays={windowDays} />
                </p>
                <div className="mt-2 flex flex-wrap gap-3 text-xs text-muted-foreground">
                  <span>
                    {data.telemetry.eligibleSampleCount.toLocaleString()} eligible samples
                  </span>
                  <span>
                    {data.telemetry.excludedSampleCount.toLocaleString()} excluded samples
                  </span>
                  {data.telemetry.automatedSampleCount !== undefined && (
                    <span>
                      {data.telemetry.automatedSampleCount.toLocaleString()} automated samples, not
                      counted as users
                    </span>
                  )}
                </div>
              </div>
              <span
                className={
                  data.telemetry.enabled
                    ? 'rounded-full bg-emerald-100 px-3 py-1 text-sm font-medium text-emerald-800'
                    : 'rounded-full bg-muted px-3 py-1 text-sm font-medium text-muted-foreground'
                }
              >
                Telemetry {data.telemetry.enabled ? 'enabled' : 'disabled'}
              </span>
            </div>
            {data.baselineArtifacts.length > 0 && (
              <div className="mt-4 border-t pt-4">
                <EvidenceLinks links={data.baselineArtifacts} />
              </div>
            )}
          </section>

          <LatestPerfWalk run={data.latestLabRun} />

          <section className="rounded-3xl border bg-card p-5" aria-label="Performance filters">
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <FilterSelect
                id="performance-filter-days"
                label="Window"
                value={String(filters.days ?? DEFAULT_WINDOW_DAYS)}
                onChange={value => {
                  const count = Number(value);
                  setFilter('days', count === DEFAULT_WINDOW_DAYS ? undefined : count);
                }}
              >
                {WINDOW_OPTIONS.map(count => (
                  <option key={count} value={count}>
                    Last {days(count)}
                  </option>
                ))}
              </FilterSelect>
              {showTenantFilter && (
                <FilterSelect
                  id="performance-filter-tenant"
                  label="Tenant"
                  value={filters.tenant ?? ''}
                  onChange={value => setFilter('tenant', value || undefined)}
                >
                  <option value="">All tenants</option>
                  {tenantOptions.map(tenant => (
                    <option key={tenant.id} value={tenant.id}>
                      {tenant.name}
                    </option>
                  ))}
                </FilterSelect>
              )}
              {FILTERS.map(filter => (
                <FilterSelect
                  key={filter.key}
                  id={`performance-filter-${filter.key}`}
                  label={filter.label}
                  value={filters[filter.key] ?? ''}
                  onChange={value => setFilter(filter.key, value || undefined)}
                >
                  <option value="">All</option>
                  {data.filters[filter.options].map(option => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </FilterSelect>
              ))}
            </div>
          </section>

          {data.interactions.length === 0 && (
            <div className="rounded-3xl border border-dashed py-12 text-center text-sm text-muted-foreground">
              No performance measurements match these filters.
            </div>
          )}
          {data.interactions.length > 0 && (
            <section className="overflow-hidden rounded-3xl border bg-card">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead rowSpan={2}>Journey and interaction</TableHead>
                    <TableHead colSpan={3} scope="colgroup" className="border-l">
                      Real users (last {days(windowDays)})
                    </TableHead>
                    <TableHead colSpan={3} scope="colgroup" className="border-l">
                      Tested (perf walk)
                    </TableHead>
                    <TableHead rowSpan={2} className="border-l">
                      Budget p50 / p95 / p99
                    </TableHead>
                    <TableHead rowSpan={2}>Status</TableHead>
                    <TableHead rowSpan={2} className="text-right">
                      Details
                    </TableHead>
                  </TableRow>
                  <TableRow>
                    <TableHead className="border-l">p50</TableHead>
                    <TableHead>p95</TableHead>
                    <TableHead>Samples</TableHead>
                    <TableHead className="border-l">p50</TableHead>
                    <TableHead>p95</TableHead>
                    <TableHead>Samples</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.interactions.map(interaction => {
                    const expanded = expandedInteraction === interaction.interactionId;
                    const statusPresentation = PERFORMANCE_STATUS_PRESENTATION[interaction.status];
                    return (
                      <Fragment key={interaction.interactionId}>
                        <TableRow>
                          <TableCell>
                            <div className="font-medium">{interaction.interactionLabel}</div>
                            <div className="text-xs text-muted-foreground">
                              {interaction.journeyLabel}
                            </div>
                          </TableCell>
                          <TableCell className="border-l">
                            {milliseconds(interaction.percentiles.p50Ms)}
                          </TableCell>
                          <TableCell>
                            <div>{milliseconds(interaction.percentiles.p95Ms)}</div>
                            {interaction.fieldTargetP95Ms !== null &&
                              interaction.fieldTargetP95Ms !== undefined && (
                                <div className="text-xs text-muted-foreground">
                                  Target {milliseconds(interaction.fieldTargetP95Ms)}
                                </div>
                              )}
                          </TableCell>
                          <TableCell>{interaction.sampleCount.toLocaleString()}</TableCell>
                          <TestedCells interaction={interaction} />
                          <TableCell className="border-l">
                            {interaction.budget.p50Ms === null ||
                            interaction.budget.p95Ms === null ||
                            interaction.budget.p99Ms === null
                              ? 'No data'
                              : `${interaction.budget.p50Ms} / ${interaction.budget.p95Ms} / ${interaction.budget.p99Ms} ms`}
                          </TableCell>
                          <TableCell>
                            <div className="flex flex-col items-start gap-1">
                              <Badge variant={statusPresentation.variant}>
                                {statusPresentation.label}
                              </Badge>
                              {interaction.status === 'insufficient-data' && minimumSamples > 0 && (
                                <span className="text-xs text-muted-foreground">
                                  Needs {minimumSamples.toLocaleString()} samples in{' '}
                                  {days(windowDays)}
                                </span>
                              )}
                              {interaction.waiver && (
                                <span className="text-xs text-muted-foreground">
                                  Waived until {shortDate(interaction.waiver.expiresAt)}
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="outline"
                              size="sm"
                              aria-expanded={expanded}
                              onClick={() =>
                                setExpandedInteraction(expanded ? null : interaction.interactionId)
                              }
                            >
                              {expanded ? 'Hide' : 'View'} {interaction.interactionLabel} details
                            </Button>
                          </TableCell>
                        </TableRow>
                        {expanded && (
                          <TableRow key={`${interaction.interactionId}-details`}>
                            <TableCell colSpan={INTERACTION_COLUMNS} className="bg-muted/30 p-5">
                              <InteractionDetails
                                interaction={interaction}
                                windowDays={windowDays}
                              />
                            </TableCell>
                          </TableRow>
                        )}
                      </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </section>
          )}
        </div>
      )}
      <div className="mt-6">
        <AgentToolLatencySection />
      </div>
    </div>
  );
}
